import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  getSmtpConfig,
  saveSmtpConfig,
  testSmtpConfig,
  deleteSmtpConfig,
  type SmtpConfigDto,
} from '@/api/smtp.api';

export function AdminSmtpPage() {
  const queryClient = useQueryClient();
  const { data: config, isLoading } = useQuery({
    queryKey: ['smtp-config'],
    queryFn: getSmtpConfig,
  });

  const [host, setHost] = useState('');
  const [port, setPort] = useState('587');
  const [secure, setSecure] = useState(false);
  const [user, setUser] = useState('');
  const [password, setPassword] = useState('');
  const [fromName, setFromName] = useState('Stammbaum');
  const [fromEmail, setFromEmail] = useState('');
  const [testTo, setTestTo] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    if (config) {
      setHost(config.host);
      setPort(String(config.port));
      setSecure(config.secure);
      setUser(config.user);
      setFromName(config.fromName);
      setFromEmail(config.fromEmail);
    }
  }, [config]);

  const saveMutation = useMutation({
    mutationFn: (dto: SmtpConfigDto) => saveSmtpConfig(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['smtp-config'] });
      toast.success('SMTP-Konfiguration gespeichert.');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const testMutation = useMutation({
    mutationFn: (to: string) => testSmtpConfig(to),
    onSuccess: (data) => toast.success(data.message),
    onError: (err: Error) => toast.error(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteSmtpConfig,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['smtp-config'] });
      setShowDeleteConfirm(false);
      toast.success('SMTP-Konfiguration gelöscht.');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    saveMutation.mutate({ host, port: Number(port), secure, user, password, fromName, fromEmail });
  };

  return (
    <div className="flex h-full flex-col bg-slate-50">
      <header className="flex h-14 shrink-0 items-center justify-between border-b border-slate-200 bg-white px-4 shadow-sm">
        <div className="flex items-center gap-2 sm:gap-4">
          <h1 className="text-base font-semibold text-slate-800 sm:text-lg">E-Mail-Einstellungen</h1>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto">
      <div className="mx-auto max-w-2xl px-6 py-8">

        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-slate-300 border-t-blue-600" />
          </div>
        ) : (
          <>
            {/* Status-Banner */}
            <div className={`mb-6 flex items-center gap-3 rounded-lg border p-4 ${config ? 'border-green-200 bg-green-50' : 'border-orange-200 bg-orange-50'}`}>
              <div className={`h-2.5 w-2.5 rounded-full ${config ? 'bg-green-500' : 'bg-orange-400'}`} />
              <span className={`text-sm font-medium ${config ? 'text-green-700' : 'text-orange-700'}`}>
                {config ? 'SMTP ist konfiguriert' : 'SMTP noch nicht konfiguriert'}
              </span>
            </div>

            <form onSubmit={handleSave} className="space-y-6 rounded-xl border border-slate-200 bg-white p-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 sm:col-span-1">
                  <label className="mb-1 block text-sm font-medium text-slate-700">SMTP-Host *</label>
                  <input
                    type="text"
                    value={host}
                    onChange={(e) => setHost(e.target.value)}
                    required
                    placeholder="smtp.example.com"
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">Port *</label>
                  <input
                    type="number"
                    value={port}
                    onChange={(e) => setPort(e.target.value)}
                    required
                    placeholder="587"
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  />
                </div>
              </div>

              <div className="flex items-center gap-3">
                <input
                  id="secure"
                  type="checkbox"
                  checked={secure}
                  onChange={(e) => setSecure(e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300 text-blue-600"
                />
                <label htmlFor="secure" className="text-sm text-slate-700">
                  SSL/TLS verwenden (Port 465) – deaktiviert für STARTTLS (Port 587)
                </label>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Benutzername *</label>
                <input
                  type="text"
                  value={user}
                  onChange={(e) => setUser(e.target.value)}
                  required
                  placeholder="user@example.com"
                  autoComplete="off"
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">
                  Passwort {config ? '(leer lassen um beizubehalten)' : '*'}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required={!config}
                    placeholder={config ? '••••••••' : 'Passwort'}
                    autoComplete="new-password"
                    className="w-full rounded-md border border-slate-300 px-3 py-2 pr-10 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? (
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88" />
                      </svg>
                    ) : (
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">Absendername *</label>
                  <input
                    type="text"
                    value={fromName}
                    onChange={(e) => setFromName(e.target.value)}
                    required
                    placeholder="Stammbaum"
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">Absender-E-Mail *</label>
                  <input
                    type="email"
                    value={fromEmail}
                    onChange={(e) => setFromEmail(e.target.value)}
                    required
                    placeholder="noreply@example.com"
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 border-t border-slate-100 pt-4">
                <button
                  type="submit"
                  disabled={saveMutation.isPending}
                  className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {saveMutation.isPending ? 'Speichern...' : 'Speichern'}
                </button>
              </div>
            </form>

            {/* Test-Email */}
            {config && (
              <div className="mt-4 rounded-xl border border-slate-200 bg-white p-6">
                <h2 className="mb-3 text-base font-semibold text-slate-900">Test-Email senden</h2>
                <div className="flex gap-2">
                  <input
                    type="email"
                    value={testTo}
                    onChange={(e) => setTestTo(e.target.value)}
                    placeholder="Empfänger-E-Mail (leer = eigene E-Mail)"
                    className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  />
                  <button
                    onClick={() => testMutation.mutate(testTo)}
                    disabled={testMutation.isPending}
                    className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  >
                    {testMutation.isPending ? 'Sende...' : 'Test senden'}
                  </button>
                </div>
              </div>
            )}

            {/* Konfiguration löschen */}
            {config && (
              <div className="mt-4 rounded-xl border border-red-100 bg-white p-6">
                <h2 className="mb-1 text-base font-semibold text-slate-900">Konfiguration entfernen</h2>
                <p className="mb-3 text-sm text-slate-500">Entfernt die SMTP-Konfiguration. E-Mail-Funktionen werden deaktiviert.</p>
                {showDeleteConfirm ? (
                  <div className="flex gap-2">
                    <button
                      onClick={() => deleteMutation.mutate()}
                      disabled={deleteMutation.isPending}
                      className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                    >
                      Ja, löschen
                    </button>
                    <button
                      onClick={() => setShowDeleteConfirm(false)}
                      className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                    >
                      Abbrechen
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="rounded-md border border-red-200 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
                  >
                    Konfiguration löschen
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>
      </main>
    </div>
  );
}
